import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Card, Spinner } from 'react-bootstrap'
import { useLocation, useParams } from 'react-router-dom'
import useFetch from '../../../../_fetch'
import { IPartnership } from '../types'

function escapeJsString(value: string) {
    return String(value || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'")
}

function renderEditableValueHighlights(line: string): React.ReactNode {
    if (!line.includes('var ORDER_')) {
        return line
    }

    const regex = /'[^']*'/g
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = regex.exec(line)) !== null) {
        if (match.index > lastIndex) {
            parts.push(line.slice(lastIndex, match.index))
        }

        parts.push(
            <span key={`${match.index}-${match[0]}`} className="partnership-print-editable-value">
                {match[0]}
            </span>,
        )

        lastIndex = match.index + match[0].length
    }

    if (lastIndex < line.length) {
        parts.push(line.slice(lastIndex))
    }

    return (
        <span className="partnership-print-editable-line">
            {parts.length > 0 ? <>{parts}</> : line}
        </span>
    )
}

const PartnershipPrint: React.FC = () => {
    const { id } = useParams()
    const location = useLocation()
    const { doFetch } = useFetch()

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [partnership, setPartnership] = useState<IPartnership | null>(null)

    useEffect(() => {
        const fetchPartnership = async () => {
            try {
                setLoading(true)
                setError('')

                const documentToken = new URLSearchParams(location.search).get('documentToken')
                const endpoint = documentToken
                    ? `/user/partnerships/${id}/document-data?token=${encodeURIComponent(documentToken)}`
                    : `/user/partnerships/${id}`
                const res = await doFetch(endpoint)
                if (res.status >= 400) throw await res.text()

                const data = await res.json()
                setPartnership(data)
            } catch (err: any) {
                setError(typeof err === 'string' ? err : 'Could not load partnership')
            } finally {
                setLoading(false)
            }
        }

        if (id) fetchPartnership()
    }, [doFetch, id, location.search])

    const entryScript = useMemo(() => {
        if (!partnership) return '<script>\n</script>'

        return `<script>
(function(){
var p=new URLSearchParams(window.location.search),r=p.get('np-ref'),c=p.get('np-click-ref');
if(!r)return;
var a=String(r).split('-');
if(a.length<3)return;
var g=(a[0]+'-'+a[1]).trim(),u=a.slice(2).join('-').trim();
if(!g||!u)return;
var v=encodeURIComponent(JSON.stringify({
agreementId:g,publisherUserId:u,
clickRef:c?String(c).trim():'',
capturedAt:new Date().toISOString()
}));
var k='np_partnership_attr='+v+
'; expires='+new Date(Date.now()+5184000000).toUTCString()+
'; path=/; SameSite=Lax';
if(window.location.protocol==='https:')k+='; Secure';
document.cookie=k;
})();
</script>`
    }, [partnership])

    const conversionScript = useMemo(() => {
        if (!partnership) return '<script>\n</script>'

        const conversionIdentifier = escapeJsString(partnership.scriptSecret || '')
        if (!conversionIdentifier) {
            return `<script>\n/* Missing script secret for this partnership. Conversion script cannot run. */\n</script>`
        }

        return `<script>
(function(){
    // ==================================================
    // CHANGE ONLY THESE 3 VALUES
    // Replace only the values inside single quotes
    // ==================================================
    var ORDER_ID = 'REPLACE_WITH_ORDER_ID';
    var ORDER_VALUE = '0.00';
    var ORDER_CURRENCY = 'DKK';
    // ==================================================
    // DO NOT EDIT BELOW THIS LINE
    // ==================================================
    var conversionIdentifier='${conversionIdentifier}';
    var raw=(document.cookie.split('; ').find(function(row){
        return row.indexOf('np_partnership_attr=')===0;
    })||'').slice('np_partnership_attr='.length);
    if(!raw)return;
    var a=null;
    try{a=raw?JSON.parse(decodeURIComponent(raw)):null;}catch(err){a=null;}
    var g=a&&a.agreementId?String(a.agreementId).trim():'';
    var u=a&&a.publisherUserId?String(a.publisherUserId).trim():'';
    var c=a&&a.clickRef?String(a.clickRef).trim():'';
    var t=a&&a.capturedAt?String(a.capturedAt).trim():'';
    if(!g||!u||!t)return;
    var o=String(ORDER_ID||'').trim();
    var m=String(ORDER_VALUE||'').trim();
    var y=String(ORDER_CURRENCY||'').toUpperCase().trim();
    if(o==='REPLACE_WITH_ORDER_ID'&&m==='0.00'&&y==='DKK')return;
    fetch('https://cdn.nordpar.com/conversion',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
            conversionIdentifier:conversionIdentifier,
            agreementId:g,publisherUserId:u,clickRef:c,
            capturedAt:t,orderId:o,amount:m,currency:y
        }),
        keepalive:true
    });
})();
</script>`
    }, [partnership])

    const serverApiExample = useMemo(() => {
        if (!partnership) return ''

        return JSON.stringify({
            conversionIdentifier: partnership.scriptSecret || 'PARTNERSHIP_SCRIPT_SECRET',
            agreementId: '<from np_partnership_attr cookie>',
            publisherUserId: '<from np_partnership_attr cookie>',
            clickRef: '<from np_partnership_attr cookie, optional>',
            capturedAt: '<from np_partnership_attr cookie>',
            orderId: 'ORDER-12345',
            amount: '99.95',
            currency: partnership.currency || 'DKK',
            eventTimestamp: new Date().toISOString(),
        }, null, 2)
    }, [partnership])

    const conversionScriptLines = useMemo(() => conversionScript.split('\n'), [conversionScript])

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" />
            </div>
        )
    }

    if (error) {
        return <Alert variant="danger" className="mb-0">{error}</Alert>
    }

    if (!partnership) {
        return <Alert variant="warning" className="mb-0">Partnership was not found.</Alert>
    }

    return (
        <div className="pe-2">
            <Card className="mb-3 partnership-print-cover-card">
                <Card.Body>
                    <div className="partnership-print-logo-wrap mb-3">
                        <img src="/nordpar-logo.png" alt="Nordpar" className="partnership-print-logo" />
                    </div>
                    <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                        <div>
                            <div className="partnership-print-eyebrow">Implementation Guide</div>
                            <h4 className="mb-0">Partnership Conversion Setup</h4>
                        </div>
                        <Badge bg="dark" className="mt-1">{partnership.type.charAt(0).toUpperCase() + partnership.type.slice(1)}</Badge>
                    </div>
                    <div className="text-muted small">Partner: {partnership.name}</div>
                    <div className="text-muted small">Agreement ID: {partnership.id}</div>
                    <div className="text-muted small">Keep this document private. It contains your Conversion Secret Key.</div>

                    <Card className="mt-3 mb-3">
                        <Card.Body>
                            <h6 className="mb-2">How the setup works</h6>
                            <p className="mb-2 text-muted">
                                Start with attribution capture, then pick your conversion reporting method. Complete one test conversion before go-live.
                            </p>
                            <ol className="mb-0 text-muted" style={{ paddingLeft: '1.2rem' }}>
                                <li>Install the Step 1 entry tracker on landing pages.</li>
                                <li>
                                    Choose one conversion reporting method:
                                    <ul className="mt-1 mb-0" style={{ paddingLeft: '1.2rem' }}>
                                        <li>Step 2A (Fastest): Client-side conversion reporting.</li>
                                        <li>Step 2B (Advanced): Server-side conversion reporting.</li>
                                    </ul>
                                </li>
                                <li>Run one real test order and verify your selected method is received correctly.</li>
                            </ol>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Body>
                            <h6 className="mb-2">What the 3 scripts do</h6>
                            <ul className="mb-0 text-muted" style={{ paddingLeft: '1.2rem' }}>
                                <li><strong>Script 1: Entry tracker</strong> captures <code>np-ref</code>/<code>np-click-ref</code> from the landing URL and stores attribution in a first-party cookie.</li>
                                <li><strong>Script 2A: Client-side conversion</strong> runs on the thank-you page and posts the conversion directly from the browser.</li>
                                <li><strong>Script 2B: Server-side conversion API</strong> sends the conversion from your backend using the same attribution data model.</li>
                            </ul>
                        </Card.Body>
                    </Card>

                    <div className="partnership-print-note mb-0">
                        <strong>2A vs 2B tracking quality:</strong> Both options track equally well when implemented correctly. The difference is operational: 2A is fastest to launch, while 2B gives stronger backend control and flexibility.
                    </div>
                </Card.Body>
            </Card>

            <div className="partnership-print-page-break" aria-hidden="true" />

            <div className="partnership-print-page">
            <Card className="mb-3">
                <Card.Header>
                    <strong>Step 1 (Required): Entry tracker script</strong>
                </Card.Header>
                <Card.Body>
                    <p className="mb-2 text-muted">
                        Add this on landing pages. It captures attribution into a first-party cookie used by both conversion options below.
                    </p>
                    <div className="partnership-print-note mb-2">
                        Place this script near the end of the <code>&lt;body&gt;</code> tag on relevant landing pages.
                    </div>
                    <p className="mb-2 text-muted">
                        Use the campaign URL provided by your publisher/Nordpar contact as-is. The script automatically stores required attribution values.
                    </p>
                    <pre className="mb-0 p-3" style={{ background: '#f7f7f7', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                        {entryScript}
                    </pre>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header>
                    <strong>Step 2A (Required, Fastest): Client-side conversion script</strong>
                </Card.Header>
                <Card.Body>
                    {partnership.type === 'click' ? (
                        <Alert variant="info" className="mb-0">Click-type partnerships do not use conversion script tracking.</Alert>
                    ) : (
                        <>
                            <p className="mb-2 text-muted">
                                Add this on your thank-you page. Update only <code>ORDER_ID</code>, <code>ORDER_VALUE</code> and <code>ORDER_CURRENCY</code>.
                            </p>
                            <div className="partnership-print-note mb-2">
                                Place this script on your order confirmation page, close to the end of <code>&lt;body&gt;</code> so order values are available.
                            </div>
                            <p className="mb-2 text-muted">
                                This is the fastest implementation and is enough for most setups.
                            </p>
                            <p className="mb-2 text-muted">
                                Tracking quality is equal to Step 2B when implemented correctly. Choose this option when speed of implementation matters most.
                            </p>
                            <p className="mb-2 text-muted">
                                Your <strong>Conversion Secret Key</strong> is private and unique for this partnership (sent as API field <code>conversionIdentifier</code>).
                            </p>
                            <pre className="mb-0 p-3" style={{ background: '#f7f7f7', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                                {conversionScriptLines.map((line, index) => (
                                    <React.Fragment key={index}>
                                        {renderEditableValueHighlights(line)}
                                        {index < conversionScriptLines.length - 1 ? '\n' : null}
                                    </React.Fragment>
                                ))}
                            </pre>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Card>
                <Card.Header>
                    <strong>Step 2B (Required, Advanced): Server-side conversion API</strong>
                </Card.Header>
                <Card.Body>
                    <p className="mb-2 text-muted">
                        Use this when you want full backend control or cannot run conversion scripts in the browser.
                    </p>
                    <p className="mb-2 text-muted">
                        Tracking quality is equal to Step 2A when implemented correctly. Choose this option for backend ownership, auditing, and integration flexibility.
                    </p>
                    <div className="partnership-print-note mb-2">
                        Your backend should read attribution values from the cookie and call the endpoint from server code (not from browser HTML).
                    </div>
                    <p className="mb-2 text-muted">
                        Attribution cookie name: <code>np_partnership_attr</code>
                    </p>
                    <p className="mb-2 text-muted">Cookie JSON attributes inside <code>np_partnership_attr</code>:</p>
                    <ul className="mb-3 text-muted" style={{ paddingLeft: '1.2rem' }}>
                        <li><strong>agreementId</strong>: agreement identifier</li>
                        <li><strong>publisherUserId</strong>: publisher user identifier</li>
                        <li><strong>clickRef</strong>: optional click reference</li>
                        <li><strong>capturedAt</strong>: ISO timestamp for attribution capture</li>
                    </ul>
                    <p className="mb-2 text-muted">Backend payload field mapping:</p>
                    <ul className="mb-3 text-muted" style={{ paddingLeft: '1.2rem' }}>
                        <li><strong>conversionIdentifier</strong>: use the partnership Conversion Secret Key</li>
                        <li><strong>agreementId</strong>: from cookie attribute <code>agreementId</code></li>
                        <li><strong>publisherUserId</strong>: from cookie attribute <code>publisherUserId</code></li>
                        <li><strong>clickRef</strong>: from cookie attribute <code>clickRef</code> (optional)</li>
                        <li><strong>capturedAt</strong>: from cookie attribute <code>capturedAt</code></li>
                        <li><strong>orderId</strong>, <strong>amount</strong>, <strong>currency</strong>: from your order system</li>
                        <li><strong>eventTimestamp</strong>: ISO timestamp for when conversion is sent</li>
                    </ul>
                    <p className="mb-2 text-muted">POST endpoint: <code>https://cdn.nordpar.com/conversion</code></p>
                    <p className="mb-2 text-muted">Most payload fields are read directly from the <code>np_partnership_attr</code> cookie.</p>
                    <p className="mb-3 text-muted">Payload example:</p>
                    <pre className="mb-0 p-3" style={{ background: '#f7f7f7', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                        {serverApiExample}
                    </pre>
                </Card.Body>
            </Card>

            </div>
        </div>
    )
}

export default PartnershipPrint
